
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast, showSuccess, showError, showInfo } from "@/hooks/use-toast";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import { devLog, devError } from "@/utils/secureLogger";
import { saveCampaignFromStorage } from "@/hooks/campaign/services/unifiedCampaignSaveService";

const PaymentCallback = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useLanguage();
  const [isProcessing, setIsProcessing] = useState(true);

  useEffect(() => {
    const processPayment = async () => {
      try {
        // Get the payment status and ID from URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        const status = urlParams.get('status');
        const paymentId = urlParams.get('id');
        
        devLog("Payment callback received:", { status, paymentId });

        if (!status || !paymentId) {
          devError("Missing payment parameters");
          showError({
            title: t('paymentIncomplete'),
            description: t('paymentParametersMissing'),
          });
          setIsProcessing(false);
          return;
        }

        if ((status === 'paid' || status === 'authorized') && paymentId) {
          // Verify the payment with our edge function
          try {
            devLog("Verifying payment with ID:", paymentId);
            const { data: verificationData, error: verifyError } = await supabase.functions
              .invoke('verify-moyasar-payment', {
                body: { payment_id: paymentId }
              });
              
            devLog("Payment verification result:", verificationData);
            
            if (verifyError) {
              devError("Payment verification error:", verifyError);
              showError({
                title: t('paymentVerificationFailed'),
                description: t('pleaseContactSupport'),
              });
              setIsProcessing(false);
              return;
            }
            
            if (verificationData?.error) {
              devError("Payment verification failed:", verificationData.error);
              
              // If the payment is still processing, show a more friendly message
              if (verificationData.status === 'pending' || verificationData.message?.includes('processing')) {
                showInfo({
                  title: t('paymentProcessing'),
                  description: verificationData.message || t('pleaseWait'),
                });
                
                // We'll consider this a success and proceed with campaign creation
                devLog("Payment is still processing, proceeding with campaign creation");
              } else {
                showError({
                  title: t('paymentVerificationFailed'),
                  description: t('pleaseContactSupport'),
                });
                setIsProcessing(false);
                return;
              }
            }
            
            if (verificationData && verificationData.status !== 'paid' && 
                verificationData.status !== 'authorized' && 
                !verificationData.message?.includes('processing')) {
              devError("Payment not confirmed as paid:", verificationData);
              showError({
                title: t('paymentIncomplete'),
                description: t('paymentNotConfirmed'),
              });
              setIsProcessing(false);
              return;
            }
          } catch (verifyErr) {
            devError("Error during payment verification:", verifyErr);
            // Continue anyway, as the verification might still be in progress
            showInfo({
              title: t('processingPayment'),
              description: t('pleaseWait'),
            });
          }
          
          showSuccess({
            title: t('paymentSuccessful'),
            description: t('processingCampaign'),
          });
          
          devLog("Saving campaign data via unified save service");
          
          // Use the unified save service - it gathers all data from localStorage
          const result = await saveCampaignFromStorage({
            status: 'approved',
            paymentId: paymentId,
            paymentType: 'online',
          });
            
          if (!result.success) {
            devError("Error saving campaign data:", result.error);
            showError({
              title: t('paymentComplete'),
              description: t('saveError'),
            });
          } else {
            devLog("Campaign data saved successfully via unified service, ID:", result.campaignId);
            showSuccess({
              title: t('campaignCreated'),
              description: t('campaignCreatedSuccess'),
            });
            
            // Set flag to show thank you popup after redirect
            localStorage.setItem('showThankYou', 'true');
          }
        } else {
          devError("Payment incomplete:", { status, paymentId });
          showError({
            title: t('paymentIncomplete'),
            description: t('tryAgain'),
          });
        }
      } catch (err) {
        devError("Error processing payment callback:", err);
        showError({
          title: t('error'),
          description: t('paymentProcessingError'),
        });
      } finally {
        setIsProcessing(false);
        
        // Redirect back to campaign page 
        navigate('/campaign');
      }
    };

    processPayment();
  }, [navigate, toast, t]);

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center bg-gradient-to-b from-background to-muted/30">
      <div className="text-center">
        <h1 className="text-2xl font-bold mb-4">{t('processingPayment')}</h1>
        <p className="text-muted-foreground">{t('pleaseWait')}</p>
        {isProcessing && (
          <div className="mt-6 flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentCallback;
